import * as angular from 'angular';
import * as $ from 'jquery';
import * as _ from 'lodash';

import { ComponentDefinition, TicketStatus, TicketStatusId, TicketViewModel, ApplicationUserViewModel } from '../../../models';
import { HeadingDefinition } from '../../../widgets/logbookTable/HeadingDefinition';
import { ITicketService, IPictureDictionary, ITicketViewModelService, IStoreService, IAuthentication } from '../../../services';
import { dropTicket } from '../ticketingKanban/dropTicket';
import { Roles } from '../../../common/constants';
import { Store, StoreElement } from '../../../stateManagement';

interface Row {
    viewModel: TicketViewModel;
    children: {
        [statusId: number]: TicketViewModel[];
    };
}

class ActionItemsStateController implements angular.IComponentController {
    authentication: IAuthentication;
    currentUserGuid: string;
    dropDisabled: { [statusId: number]: boolean };
    headings: HeadingDefinition[];
    isLoading: boolean;
    loadingMessage: string;
    originalTicket: TicketViewModel;
    parent: TicketViewModel;
    selectedTicketViewModel: TicketViewModel;
    saveTicket: ({ ticket: Ticket }) => angular.IPromise<void>;
    ticketStatusList: TicketStatus[];
    ticketViewModelList: TicketViewModel[];
    users: ApplicationUserViewModel[];
    userPictures: IPictureDictionary;
    visibleTickets: Row[];
    visibleTicketStatusList: TicketStatus[];

    private _acceptedStatuses = [TicketStatusId.Assigned, TicketStatusId.New];
    private _children: TicketViewModel[];
    private _visibleTickets: TicketViewModel[];

    static $inject = ['ticketService', 'ticketViewModelService', 'authenticationService'];

    constructor(
        private _ticketService: ITicketService,
        private _ticketViewModelService: ITicketViewModelService,
        private _storeService: IStoreService
    ) {
    }

    $onInit() {
        this._storeService.register(
            store => this.refreshStore(store),
            [StoreElement.Authentication]
        );

        this._children = [];
    }
    
    $onChanges(changesObj: angular.IOnChangesObject) {
        // Filter tickets to only display new and assigned tickets
        if (changesObj['ticketViewModelList'] && changesObj['ticketViewModelList'].currentValue) {
            this._visibleTickets = this.ticketViewModelList
                .filter(t => this._acceptedStatuses.indexOf(t.model.statusId) >= 0);
            this.visibleTickets = this.mapRows(this._visibleTickets, this._children || []);
            this.refreshChildren(false);
        }

        // Initialize action items layout based on their status
        if (changesObj['ticketStatusList'] && changesObj['ticketStatusList'].currentValue) {
            this.visibleTicketStatusList = _.filter(this.ticketStatusList, ticketStatus => ticketStatus && ticketStatus.isVisible);
            const headings: HeadingDefinition[] = [{ label: '' }]
                .concat(this.visibleTicketStatusList.map(d => { return { label: d.name }; }));
            headings.forEach(h => h.style = { width: 100 / headings.length + '%' });
            this.headings = headings;
            const dropDisabled = {};
            this.visibleTicketStatusList.forEach(d => dropDisabled[d.id] = false);
            this.dropDisabled = dropDisabled;
        }

        if (changesObj['users'] && changesObj['users'].currentValue) {
            if (changesObj['users'].currentValue && changesObj['users'].currentValue.length > 0 && !this._children) {
                this.refreshChildren(true);
            }
        }
    }

    // Create new action item (Ticket with a parentId)
    createNew(parent: TicketViewModel) {
        this.parent = parent;
        this.selectedTicketViewModel = null;
        $('#child-ticket-modal').modal('show');
    }

    // Refresh action items after saving
    saved() {
        this.refreshChildren(true);
    }

    // Check if drop is allowed based on assignable statuses of the ticket dragged
    onDragStart() {
        return (child: TicketViewModel) => {
            const isMaster = this.userInRole([Roles.master]);
            const dropDisabled = {};
            this.visibleTicketStatusList
                .forEach(d => dropDisabled[d.id] = child.assignableStatuses.indexOf(d.id) < 0);
            if (!isMaster) {
                this.dropDisabled = dropDisabled;
            }
            this.select(child);
        };
    }

    // Select a ticket
    select(child: TicketViewModel, parent?: Row) {
        this.selectedTicketViewModel = child;
        if (parent) {
            // this.parentId = parent.viewModel.model.id;
            this.parent = parent.viewModel;
        }
    }

    // Update ticket when dropped
    onDrop(data, statusid) {
        dropTicket(data, statusid, this.currentUserGuid, this._children, this.saveTicket)
            .then(() => this.refreshChildren(true));
    }

    private refreshStore(store: Store): void {
        this.authentication = store.authentication.value;
        this.currentUserGuid = this.authentication.userGuid;
    }

    // Refresh action items
    private refreshChildren(force: boolean) {
        this._ticketService
            .getChildrenWithAssignableStatuses(force)
            .then(data => {
                this._children = this._ticketViewModelService.buildViewModels(data.tickets, this.users, this.userPictures, data.assignableStatuses);
                this.visibleTickets = this.mapRows(this._visibleTickets || [], this._children);
            });
    }

    // Map action items to theirs parent tickets
    private mapRows(tickets: TicketViewModel[], children: TicketViewModel[]): Row[] {
        const result = tickets.map(t => {
            const allChildren = children
                .filter(c => c.model.parentTicketId === t.model.id);
            const childrenDic: { [statusId: number]: TicketViewModel[] } = {};
            allChildren.forEach(c => {
                if (!childrenDic[c.model.statusId]) {
                    childrenDic[c.model.statusId] = [];
                }
                childrenDic[c.model.statusId].push(c);
            });
            return {
                viewModel: t,
                children: childrenDic
            };
        });
        return result;
    }

    // Check if user has at least an allowed role
    private userInRole(roles: string[]): boolean {
        return _.some(this.authentication.roles, r => roles.indexOf(r) >= 0);
    }
}

export const wtActionItemsState: ComponentDefinition = {
    componentId: 'wtActionItemsState',
    controller: ActionItemsStateController,
    template: require('./actionItemsState.component.html'),
    controllerAs: 'vm',
    bindings: {
        ticketViewModelList: '<',
        ticketStatusList: '<',
        selectedTicketViewModel: '=?',
        originalTicketViewModel: '=?',
        saveTicket: '&',
        users: '<',
        userPictures: '<',
        isLoading: '<',
        message: '<'
    }
};